import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Grid,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Paper,
  Stack,
  Avatar,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { contractorsApi, classificationApi, networksApi, sectionsApi } from '../../services/api';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const ContractorCapabilitiesModal = ({ open, company, onClose, onCapabilitiesUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Data states
  const [defectTypes, setDefectTypes] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [sections, setSections] = useState([]);
  
  // Current capabilities
  const [selectedDefectTypes, setSelectedDefectTypes] = useState([]);
  const [currentCapabilities, setCurrentCapabilities] = useState([]);
  
  // Jurisdiction states
  const [jurisdictions, setJurisdictions] = useState([]);
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const [networkSections, setNetworkSections] = useState({});

  useEffect(() => {
    if (open && company) {
      fetchData();
    }
  }, [open, company]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const defectTypesResponse = await classificationApi.getDefectTypes({ 
        asset_type_id: 1,
        organization_id: company.organization_id 
      });
      setDefectTypes(defectTypesResponse.data);

      const networksResponse = await networksApi.getAll();
      setNetworks(networksResponse.data);

      const capabilitiesResponse = await contractorsApi.getCapabilities(company.id);
      const capabilitiesData = capabilitiesResponse.data.data || capabilitiesResponse.data;
      setCurrentCapabilities(capabilitiesData.defect_type_capabilities || []);
      setSelectedDefectTypes(capabilitiesData.defect_type_capabilities?.map(cap => cap.defect_type_id) || []);

      const jurisdictionsResponse = await contractorsApi.getJurisdictions(company.id);
      const currentJurisdictions = jurisdictionsResponse.data.data || jurisdictionsResponse.data || [];
      setJurisdictions(Array.isArray(currentJurisdictions) ? currentJurisdictions : []);
      
      const networkIds = currentJurisdictions
        .filter(j => j.jurisdiction_type === 'network')
        .map(j => j.network_id);
      setSelectedNetworks(networkIds);

      for (const networkId of networkIds) {
        await fetchSectionsForNetwork(networkId);
      }

    } catch (error) {
      console.error('Failed to fetch capabilities data:', error);
      setError('Failed to load capabilities data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionsForNetwork = async (networkId) => {
    try {
      const response = await sectionsApi.getAll({ network_id: networkId });
      setNetworkSections(prev => ({
        ...prev,
        [networkId]: response.data
      }));
    } catch (error) {
      console.error(`Failed to fetch sections for network ${networkId}:`, error);
    }
  };

  const handleDefectTypesChange = (event) => {
    const value = event.target.value;
    setSelectedDefectTypes(typeof value === 'string' ? value.split(',') : value);
  };

  const handleNetworksChange = async (event) => {
    const value = event.target.value;
    const newNetworkIds = typeof value === 'string' ? value.split(',') : value;
    setSelectedNetworks(newNetworkIds);

    for (const networkId of newNetworkIds) {
      if (!networkSections[networkId]) {
        await fetchSectionsForNetwork(networkId);
      }
    }

    setJurisdictions(prev => 
      prev.filter(j => 
        j.jurisdiction_type === 'network' || 
        newNetworkIds.includes(j.network_id)
      )
    );

    const networkJurisdictions = newNetworkIds.map(networkId => ({
      network_id: parseInt(networkId),
      section_id: null,
      jurisdiction_type: 'network',
      coverage_percentage: 100
    }));

    setJurisdictions(prev => {
      const withoutNetworks = prev.filter(j => j.jurisdiction_type !== 'network');
      return [...withoutNetworks, ...networkJurisdictions];
    });
  };

  const handleSectionToggle = (networkId, sectionId) => {
    setJurisdictions(prev => {
      const sectionJurisdiction = {
        network_id: parseInt(networkId),
        section_id: parseInt(sectionId),
        jurisdiction_type: 'section',
        coverage_percentage: 100
      };

      const existingIndex = prev.findIndex(j => 
        j.jurisdiction_type === 'section' && 
        j.network_id === parseInt(networkId) && 
        j.section_id === parseInt(sectionId)
      );

      if (existingIndex >= 0) {
        return prev.filter((_, index) => index !== existingIndex);
      } else {
        return [...prev, sectionJurisdiction];
      }
    });
  };

  const isSectionSelected = (networkId, sectionId) => {
    return jurisdictions.some(j => 
      j.jurisdiction_type === 'section' && 
      j.network_id === parseInt(networkId) && 
      j.section_id === parseInt(sectionId)
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (selectedDefectTypes.length > 0) {
        const capabilities = selectedDefectTypes.map(defectTypeId => ({
          defect_type_id: parseInt(defectTypeId),
          experience_level: 'standard',
          certification_required: false,
          notes: ''
        }));
        
        await contractorsApi.setCapabilities(company.id, { capabilities });
      }

      if (jurisdictions.length > 0) {
        await contractorsApi.setJurisdictions(company.id, { jurisdictions });
      }

      onCapabilitiesUpdated();
      onClose();
    } catch (error) {
      console.error('Failed to save capabilities:', error);
      setError(error.response?.data?.message || 'Failed to save capabilities. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          height: '95vh',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          m: 1
        }
      }}
    >
      {/* Sticky Header */}
      <DialogTitle 
        sx={{ 
          p: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <SettingsIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                  Manage Capabilities
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure capabilities for {company?.name}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleClose} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Company Summary Card */}
          <Paper 
            elevation={0}
            sx={{ 
              mt: 2,
              p: 2,
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              borderRadius: 2
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'secondary.dark' }}>
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {company?.name}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Contact: {company?.contact_person} • {company?.email}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </DialogTitle>

      {/* Scrollable Content */}
      <DialogContent 
        sx={{ 
          flex: 1,
          p: 0,
          overflow: 'auto'
        }}
      >
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 8,
              bgcolor: 'grey.50',
              borderRadius: 2
            }}>
              <CircularProgress size={32} sx={{ mr: 2 }} />
              <Typography variant="h6">Loading capabilities data...</Typography>
            </Box>
          ) : (
            <Stack spacing={4}>
              {/* Defect Type Capabilities Section */}
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <BuildIcon color="primary" />
                  Defect Type Capabilities
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select Defect Types</InputLabel>
                  <Select
                    multiple
                    value={selectedDefectTypes}
                    onChange={handleDefectTypesChange}
                    input={<OutlinedInput label="Select Defect Types" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const defectType = defectTypes.find(dt => dt.id === value);
                          return (
                            <Chip 
                              key={value} 
                              label={defectType?.name || value} 
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {defectTypes.map((defectType) => (
                      <MenuItem key={defectType.id} value={defectType.id}>
                        <Checkbox checked={selectedDefectTypes.indexOf(defectType.id) > -1} />
                        <ListItemText primary={defectType.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'success.light', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="body2" color="success.dark" sx={{ fontWeight: 500 }}>
                    Selected: {selectedDefectTypes.length} defect types
                  </Typography>
                </Box>
              </Paper>

              {/* Location Jurisdictions Section */}
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <LocationIcon color="primary" />
                  Location Jurisdictions
                </Typography>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Networks</InputLabel>
                  <Select
                    multiple
                    value={selectedNetworks}
                    onChange={handleNetworksChange}
                    input={<OutlinedInput label="Select Networks" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const network = networks.find(n => n.id === parseInt(value));
                          return (
                            <Chip 
                              key={value} 
                              label={network?.name || value} 
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {networks.map((network) => (
                      <MenuItem key={network.id} value={network.id}>
                        <Checkbox checked={selectedNetworks.indexOf(network.id) > -1} />
                        <ListItemText primary={network.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Sections for selected networks */}
                <Stack spacing={3}>
                  {selectedNetworks.map(networkId => {
                    const network = networks.find(n => n.id === parseInt(networkId));
                    const sections = networkSections[networkId] || [];
                    
                    return (
                      <Paper key={networkId} elevation={0} sx={{ p: 2, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'grey.300' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon fontSize="small" color="secondary" />
                          {network?.name} - Sections (Optional)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Leave empty to cover entire network, or select specific sections
                        </Typography>
                        
                        <Box sx={{ 
                          maxHeight: 200, 
                          overflow: 'auto', 
                          border: '1px solid', 
                          borderColor: 'divider', 
                          borderRadius: 1, 
                          p: 2,
                          bgcolor: 'background.paper'
                        }}>
                          {sections.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                              No sections available for this network
                            </Typography>
                          ) : (
                            <Grid container spacing={1}>
                              {sections.map(section => (
                                <Grid item xs={12} sm={6} md={4} key={section.id}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isSectionSelected(networkId, section.id)}
                                        onChange={() => handleSectionToggle(networkId, section.id)}
                                        size="small"
                                      />
                                    }
                                    label={section.name}
                                    sx={{ m: 0 }}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>

                <Box sx={{ 
                  mt: 2,
                  p: 2, 
                  bgcolor: 'info.light', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <CheckCircleIcon color="info" fontSize="small" />
                  <Typography variant="body2" color="info.dark" sx={{ fontWeight: 500 }}>
                    Selected: {selectedNetworks.length} networks, {jurisdictions.filter(j => j.jurisdiction_type === 'section').length} specific sections
                  </Typography>
                </Box>
              </Paper>
            </Stack>
          )}
        </Box>
      </DialogContent>

      {/* Sticky Footer */}
      <DialogActions 
        sx={{ 
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 3
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={saving}
          size="large"
          variant="outlined"
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained" 
          disabled={saving || loading}
          size="large"
          sx={{ minWidth: 160 }}
        >
          {saving ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={20} color="inherit" />
              <Typography>Saving...</Typography>
            </Stack>
          ) : (
            'Save Capabilities'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractorCapabilitiesModal; 